import { z } from 'zod';
import { CrmRefSchema } from '../auth/crm-registration.js';

const uuid = z.string().uuid();
const email = z.string().email().max(254).transform(value => value.toLowerCase());
/** Server-asserted CRM contact scope, never authority to attach an application account. */
export const CrmProspectReadRequestSchema = z.object({
  workspace_id: uuid, deal_id: uuid, company_id: uuid, contact_id: uuid, crm_ref: CrmRefSchema,
  identity_fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  contact_emails: z.array(email).min(1).max(20),
}).strict().superRefine((value, ctx) => {
  if (new Set(value.contact_emails).size !== value.contact_emails.length)
    ctx.addIssue({ code: 'custom', message: 'CRM prospect contact emails must be unique' });
});
export type CrmProspectReadRequest = z.infer<typeof CrmProspectReadRequestSchema>;

export const CrmProspectCabinetSchema = z.object({
  cabinet_id: uuid, billing_managed_by_cabinet_id: uuid.nullable(), shared_customer: z.boolean(),
  stripe_customer_id: z.string().regex(/^cus_[A-Za-z0-9]+$/).nullable(),
  stripe_checkout_session_id: z.string().regex(/^cs_[A-Za-z0-9_]+$/).nullable(),
  subscriptions: z.array(z.object({
    stripe_subscription_id: z.string().regex(/^sub_[A-Za-z0-9]+$/).nullable(),
    stripe_customer_id: z.string().regex(/^cus_[A-Za-z0-9]+$/).nullable(),
    status: z.string().min(1).max(64),
  }).strict()).max(200),
}).strict();
/** No user identity, email inventory, monetary amount or historical registration is returned. */
export const CrmProspectReadSnapshotSchema = z.object({
  account: z.enum(['absent', 'present', 'ambiguous']),
  reason: z.enum(['complete_negative_inventory', 'unique_current_membership', 'identity_or_billing_ambiguous']),
  ovh_complete: z.literal(true), auth_complete: z.literal(true),
  cabinet: CrmProspectCabinetSchema.nullable(),
}).strict().superRefine((value, ctx) => {
  if ((value.account === 'present') !== (value.cabinet !== null)
    || (value.account === 'absent' && value.reason !== 'complete_negative_inventory')
    || (value.account === 'present' && value.reason !== 'unique_current_membership')
    || (value.account === 'ambiguous' && value.reason !== 'identity_or_billing_ambiguous'))
    ctx.addIssue({ code: 'custom', message: 'CRM prospect account outcome contradicts its evidence' });
});
export type CrmProspectReadSnapshot = z.infer<typeof CrmProspectReadSnapshotSchema>;
export const CrmProspectReadResponseSchema = z.object({
  version: z.literal(1), environment: z.enum(['production', 'staging']), scope: CrmProspectReadRequestSchema,
  started_at: z.string().datetime({ offset: true }), completed_at: z.string().datetime({ offset: true }),
  snapshot: CrmProspectReadSnapshotSchema,
}).strict();
